import { useMemo } from 'react'
import { Popover as RadPopover } from 'radix-ui'
import AddFriendIcon from '@/CustomIcons/AddFriendIcon'
import BellIcon from '@/CustomIcons/BellIcon'
import ClearIcon from '@/CustomIcons/ClearIcon'
import RadioCircleIcon from '@/CustomIcons/RadioCircleIcon'
import { Button } from '@/UIComponents2/Button'
import { AppEvents, useUserContext } from '@/UserContext'
import { cn } from '@/utlis/cn'
import { timeAgo } from '@/services/TimeAgo'
import { useCommunityFeature } from '@/services/useCommunityFeature'
import { useHistory } from 'react-router-dom'
import { useMutate } from 'restful-react'
import { IconCircle } from './CircleIcon'
import UnheartIcon from '@/CustomIcons/UnheartIcon'
import HeartIcon from '@/CustomIcons/HeartIcon'
import { Badge } from '@/UIComponents2/Badge'
import { Trans, useTranslation } from 'react-i18next'
import { Username } from './Username'
import '@/components/popover-animation.css'

const actionableEventKinds = ['collaborator_invitation']

type NotificationsPopoverProps = {
    className?: string
    children: React.ReactNode
}

export const NotificationsPopover = (props: NotificationsPopoverProps) => {
    const { t } = useTranslation()
    const [userState, userDispatch] = useUserContext()

    const actionableEventCount = useMemo(() => {
        let count = 0
        for (const ev of userState.appEvents) {
            if (actionableEventKinds.includes(ev.code) || !ev.isRead) {
                count++
            }
        }
        return count
    }, [userState.appEvents])

    const { mutate: patchAppEvents, error: patchAppEventsError } = useMutate({
        verb: 'PATCH',
        path: ({ userUrn }) => `/v1/users/_me/events`,
    })

    const readAllAppEvents = () => {
        patchAppEvents({}, { queryParams: { readall: '1' } })

        const appEvents_next = userState.appEvents.map((ev) => ({ ...ev, isRead: true }))
        userDispatch({
            type: 'set_app_events',
            value: appEvents_next,
        })
    }

    const clearAllAppEvents = () => {
        patchAppEvents({}, { queryParams: { clear: '1' } })
        userDispatch({
            type: 'set_app_events',
            value: [],
        })
    }

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            readAllAppEvents()
        }
    }

    const style =
        'grid grid-rows-[min-content_1fr] w-[90vw] md:w-[340px] max-h-[60vh] md:max-h-[70vh] p-4 bg-white/5 z-30'
    const border = 'border-1 border-white/10 rounded-[20px]'
    const bg = 'bg-muse-menu'

    return (
        <RadPopover.Root onOpenChange={(open) => handleOpenChange(open)} modal={false}>
            <RadPopover.Trigger className="relative" asChild>
                <div>
                    {props.children}
                    {actionableEventCount > 0 && (
                        <Badge>{actionableEventCount > 9 ? '9+' : actionableEventCount}</Badge>
                    )}
                </div>
            </RadPopover.Trigger>
            <RadPopover.Content
                className={cn('popover-content', style, border, bg, props.className)}
                sideOffset={5}
                collisionPadding={10}
            >
                <div className="flex justify-between items-center gap-2 pb-4">
                    <div className="flex gap-2 items-center">
                        <BellIcon />
                        <span className="t-base-medium">{t('Notifications')}</span>
                    </div>
                    {userState.appEvents.length > 0 && (
                        <Button className="flex gap-1 items-center" onClick={clearAllAppEvents}>
                            <ClearIcon style={{ fontSize: '1.4rem' }} />
                            <span className="t-base-light opacity-65">{t('Clear')}</span>
                        </Button>
                    )}
                </div>
                <div className="flex flex-col gap-2 overflow-y-auto h-full pr-2">
                    {userState.appEvents.length === 0 && <EmptyNotifications />}
                    {userState.appEvents.map((ev) => (
                        <NotificationItem key={ev.uuid} event={ev} />
                    ))}
                </div>
            </RadPopover.Content>
        </RadPopover.Root>
    )
}

type NotificationItemProps = {
    className?: string
    event: AppEvents
}
function NotificationItem({ className, event }: NotificationItemProps) {
    if (event.code === 'collaborator_invitation') {
        return (
            <FriendRequest username={event.username} sentAt={event.timestamp} userId={event.userUrn} isRead={false} />
        )
    } else if (event.code === 'session_invitation') {
        return (
            <SessionInvite
                inviter={event.username}
                joinCode={event.roomJoinCode}
                sentAt={event.timestamp}
                eventId={event.uuid}
                isRead={event.isRead}
            />
        )
    } else if (event.code === 'you_denied_collaborator_invitation') {
        return (
            <BasicMessage isRead={event.isRead} sentAt={event.timestamp} icon={<UnheartIcon />}>
                <span>
                    <Trans
                        i18nKey="You declined a friend request from <0/>."
                        values={{ username: event.username }}
                        components={[
                            <Username
                                maxLength={18}
                                username={event.username}
                                className="t-base-medium text-muse-yellow"
                            />,
                        ]}
                    />
                </span>
            </BasicMessage>
        )
    } else if (event.code === 'you_accepted_collaborator_invitation') {
        return (
            <BasicMessage isRead={event.isRead} sentAt={event.timestamp} icon={<HeartIcon />}>
                <span>
                    <Trans
                        i18nKey="You accepted <0/>'s friend request."
                        values={{ username: event.username }}
                        components={[
                            <Username
                                maxLength={18}
                                username={event.username}
                                className="t-base-medium text-muse-yellow"
                            />,
                        ]}
                    />
                </span>
            </BasicMessage>
        )
    } else if (event.code === 'collaborator_invitation_denied') {
        return (
            <BasicMessage isRead={event.isRead} sentAt={event.timestamp} icon={<UnheartIcon />}>
                <span>
                    <Trans
                        i18nKey="</0> declined your friend request."
                        values={{ username: event.username }}
                        components={[
                            <Username
                                maxLength={18}
                                username={event.username}
                                className="t-base-medium text-muse-yellow"
                            />,
                        ]}
                    />
                </span>
            </BasicMessage>
        )
    } else if (event.code === 'collaborator_invitation_accepted') {
        return (
            <BasicMessage isRead={event.isRead} sentAt={event.timestamp} icon={<HeartIcon />}>
                <span>
                    <Trans
                        i18nKey="<0/> accepted your friend request!"
                        values={{ username: event.username }}
                        components={[
                            <Username
                                maxLength={18}
                                username={event.username}
                                className="t-base-medium text-muse-yellow"
                            />,
                        ]}
                    />
                </span>
            </BasicMessage>
        )
    }

    return (
        <div>
            <pre>{event.code}</pre>
        </div>
    )
}

type SessionInviteProps = {
    className?: string
    inviter: string
    joinCode: string
    sentAt: string
    eventId: string
    isRead: boolean
}
function SessionInvite(props: SessionInviteProps) {
    const { t } = useTranslation()
    const community = useCommunityFeature()
    const history = useHistory()

    const border = 'border-muse-toplit-low rounded-[16px]'
    const bg = 'bg-[rgba(255,255,255,0.03)]'
    const layout = 'flex flex-col p-2 gap-3'
    return (
        <div className={cn(border, bg, layout, props.className)}>
            <div className="flex justify-between items-center px-3">
                <RadioCircleIcon className={cn({ 'text-[#FCDD40]': !props.isRead })} />
                <div className="flex items-center gap-2 text-muse-fg/50 t-sm-light">
                    {!props.isRead && <div className="w-[5px] h-[5px] rounded-full bg-[#FCDD40]"></div>}
                    {timeAgo.format(new Date(props.sentAt))}
                </div>
            </div>
            <div className="px-3">
                <Trans
                    i18nKey="<0/> invited you to join session with code <1>{{joinCode}}</1>"
                    values={{ inviter: props.inviter, joinCode: props.joinCode }}
                    components={[
                        <Username maxLength={18} username={props.inviter} className="t-base-medium text-muse-yellow" />,
                        <span className="text-muse-yellow t-base-medium" />,
                    ]}
                />
            </div>
            <div className="flex justify-between items-center gap-1">
                <Button
                    variant="glow"
                    className="py-[8px] w-full t-base-light"
                    onClick={() => {
                        community.deleteSessionInvite(props.eventId)
                    }}
                >
                    {t('Decline')}
                </Button>
                <Button
                    variant="primary"
                    className="py-[8px] w-full"
                    onClick={() => {
                        history.push(`/join?code=${props.joinCode}`)
                    }}
                >
                    {t('Join')}
                </Button>
            </div>
        </div>
    )
}

type FriendRequestProps = {
    className?: string
    username: string
    userId: string
    sentAt: string
    isRead: boolean
}
function FriendRequest(props: FriendRequestProps) {
    const { t } = useTranslation()
    const community = useCommunityFeature()

    const border = 'border-muse-toplit-low rounded-[16px]'
    const bg = 'bg-[rgba(255,255,255,0.03)]'
    const layout = 'flex flex-col p-2 gap-3'
    return (
        <div className={cn(border, bg, layout, props.className)}>
            <div className="flex justify-between items-center px-3">
                <AddFriendIcon className={cn({ 'text-[#FCDD40]': !props.isRead })} />
                <div className="flex items-center gap-2 text-muse-fg/50 t-sm-light">
                    {!props.isRead && <div className="w-[5px] h-[5px] rounded-full bg-[#FCDD40]"></div>}
                    {timeAgo.format(new Date(props.sentAt))}
                </div>
            </div>
            <div className="px-3">
                <Trans
                    i18nKey="<0/> wants to add you as a friend"
                    values={{ username: props.username }}
                    components={[
                        <Username
                            maxLength={18}
                            username={props.username}
                            className="t-base-medium text-muse-yellow"
                        />,
                    ]}
                />
            </div>
            <div className="flex justify-between items-center gap-1">
                <Button
                    variant="glow"
                    className="py-[8px] w-full t-base-light"
                    onClick={() => community.denyCollaboratorInvitation(props.userId)}
                >
                    {t('Decline')}
                </Button>
                <Button
                    variant="primary"
                    className="py-[8px] w-full"
                    onClick={() => community.acceptCollaboratorInvitation(props.userId)}
                >
                    {t('Accept')}
                </Button>
            </div>
        </div>
    )
}

type BasicMessageProps = {
    className?: string
    children?: React.ReactNode
    isRead: boolean
    sentAt: string
    icon?: React.ReactNode
}
function BasicMessage(props: BasicMessageProps) {
    const border = 'border-muse-toplit-low rounded-[16px]'
    const bg = 'bg-[rgba(255,255,255,0.03)]'
    const layout = 'flex flex-col p-2 gap-3'
    return (
        <div className={cn(border, bg, layout, props.className)}>
            <div className="flex justify-between items-center px-3">
                <div className={cn({ 'text-[#FCDD40]': !props.isRead })}>{props.icon}</div>
                <div className="flex items-center gap-2 text-muse-fg/50 t-sm-light">
                    {!props.isRead && <div className="w-[5px] h-[5px] rounded-full bg-[#FCDD40]"></div>}
                    {timeAgo.format(new Date(props.sentAt))}
                </div>
            </div>
            <div className="px-3">{props.children}</div>
        </div>
    )
}

function EmptyNotifications() {
    const { t } = useTranslation()

    return (
        <div className="flex flex-col items-center justify-center gap-2 t-lg h-[250px]">
            <IconCircle className="w-16 h-16">
                <BellIcon />
            </IconCircle>

            <span>{t('No notifications!')}</span>
        </div>
    )
}
